import React, { useEffect, useState } from 'react'
import { ThreeCircles } from 'react-loader-spinner'
import DashboardSideNav from '../../adminComponents/DashboardComponents/DashboardSideNav'
import DashboardHeader from '../../adminComponents/DashboardComponents/DashboardHeader'
import Swal from 'sweetalert2'
import { GetCampaignConfig, UpdateCampaignConfig } from '../../service/service'
import { fetchCharidyCampaign } from '../../service/charidy'

const emptyForm = {
    enabled: false,
    campaign_shortlink: '',
    donate_url: '',
    donor_goal: 1000,
    popup_enabled: true,
    popup_image_url: '',
    popup_headline: '',
    popup_text: '',
};

function CampaignManagement() {
    const [isLoading, setIsLoading] = useState(false)
    const [formData, setFormData] = useState(emptyForm)
    const [verifyResult, setVerifyResult] = useState(null)
    const [verifyError, setVerifyError] = useState('')
    const [loadState, setLoadState] = useState('loading') // 'loading' | 'loaded' | 'error'

    useEffect(() => {
        GetCampaignConfig().then((result) => {
            if (result?.status && result.data) {
                setFormData({
                    enabled: !!result.data.enabled,
                    campaign_shortlink: result.data.campaign_shortlink || '',
                    donate_url: result.data.donate_url || '',
                    donor_goal: Number(result.data.donor_goal) > 0 ? Number(result.data.donor_goal) : 1000,
                    popup_enabled: result.data.popup_enabled !== false,
                    popup_image_url: result.data.popup_image_url || '',
                    popup_headline: result.data.popup_headline || '',
                    popup_text: result.data.popup_text || '',
                })
                setLoadState('loaded')
            } else {
                setLoadState('error')
            }
        }).catch(() => { setLoadState('error') })
    }, [])

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }))
    }

    const verifyShortlink = () => {
        setVerifyResult(null)
        setVerifyError('')
        if (!formData.campaign_shortlink.trim()) {
            setVerifyError('Enter a shortlink first.')
            return
        }
        setIsLoading(true)
        fetchCharidyCampaign(formData.campaign_shortlink.trim())
            .then((campaign) => setVerifyResult(campaign))
            .catch(() => setVerifyError('Could not find that campaign on Charidy. Check the shortlink.'))
            .finally(() => setIsLoading(false))
    }

    const submitData = () => {
        if (loadState !== 'loaded') {
            return
        }
        const shortlink = formData.campaign_shortlink.trim()
        const payload = {
            enabled: formData.enabled,
            campaign_shortlink: shortlink,
            donate_url: formData.donate_url.trim() || (shortlink ? `https://charidy.com/${shortlink}` : ''),
            donor_goal: Number(formData.donor_goal) > 0 ? Number(formData.donor_goal) : 1000,
            popup_enabled: !!formData.popup_enabled,
            popup_image_url: formData.popup_image_url.trim() || null,
            popup_headline: formData.popup_headline.trim() || null,
            popup_text: formData.popup_text.trim() || null,
        }
        setIsLoading(true)
        UpdateCampaignConfig(payload).then((result) => {
            setIsLoading(false)
            if (result?.status) {
                setFormData((prev) => ({ ...prev, donate_url: payload.donate_url }))
                Swal.fire({
                    icon: 'success',
                    title: 'Campaign Settings Updated Successfully.',
                    showConfirmButton: false,
                    timer: 1500,
                })
            }
        }).catch((error) => {
            setIsLoading(false)
            Swal.fire({ icon: 'error', title: error.message || 'Update failed.' })
        })
    }

    const fmt = (n) => (typeof n === 'number' ? `$${n.toLocaleString('en-US')}` : n)

    return (
        <>
            {isLoading &&
                <div className="overlay">
                    <div className="loaderImage-container">
                        <ThreeCircles height="100" width="100" color="#F9F9F9" visible={true} ariaLabel="three-circles-rotating" />
                    </div>
                </div>
            }
            <div className='bg-whiteGrey'>
                <DashboardSideNav />
                <main className="bg-[#D2D6DC] min-h-screen h-full sm:ml-[246px] lg:ml-[310px]">
                    <DashboardHeader title={'Campaign'} subtitle={'Campaign Settings'} />
                    <div className='px-4 py-6 md:px-8'>
                        <h1 className='mb-6 text-2xl font-extrabold text-primaryDark'>Campaign Settings</h1>
                        <div className='max-w-2xl space-y-5 rounded-lg bg-white p-6 shadow'>
                            {loadState === 'error' && (
                                <p className='rounded border border-red-300 bg-red-50 p-3 text-sm font-semibold text-red-600'>
                                    Could not load the current campaign settings. Refresh the page — saving now could overwrite them.
                                </p>
                            )}
                            <div className='flex items-center gap-3'>
                                <input id='campaign-enabled' type='checkbox' name='enabled'
                                    checked={formData.enabled} onChange={handleChange}
                                    className='h-5 w-5' />
                                <label htmlFor='campaign-enabled' className='font-semibold text-primaryDark'>
                                    Campaign mode enabled
                                </label>
                            </div>
                            <div>
                                <label htmlFor='campaign-shortlink' className='mb-1 block font-semibold text-primaryDark'>
                                    Charidy shortlink
                                </label>
                                <div className='flex gap-2'>
                                    <input id='campaign-shortlink' type='text' name='campaign_shortlink'
                                        value={formData.campaign_shortlink} onChange={handleChange}
                                        placeholder='insidechassidus'
                                        className='w-full rounded border border-gray-300 px-3 py-2' />
                                    <button type='button' onClick={verifyShortlink}
                                        className='rounded bg-primaryDark px-4 py-2 font-semibold text-white'>
                                        Verify
                                    </button>
                                </div>
                                <p className='mt-1 text-sm text-gray-500'>
                                    The part after charidy.com/ in your campaign URL.
                                </p>
                                {verifyResult && (
                                    <div className='mt-2 rounded border border-green-300 bg-green-50 p-3 text-sm text-green-900'>
                                        <p className='font-bold'>{verifyResult.title}</p>
                                        <p>Goal: {fmt(verifyResult.goal)} · Raised so far: {fmt(verifyResult.totalRaised)}</p>
                                        <p>Runs {verifyResult.startDate} → {verifyResult.endDate}</p>
                                    </div>
                                )}
                                {verifyError && (
                                    <p className='mt-2 text-sm font-semibold text-red-600'>{verifyError}</p>
                                )}
                            </div>
                            <div>
                                <label htmlFor='campaign-donate-url' className='mb-1 block font-semibold text-primaryDark'>
                                    Donate URL
                                </label>
                                <input id='campaign-donate-url' type='text' name='donate_url'
                                    value={formData.donate_url} onChange={handleChange}
                                    placeholder='Defaults to https://charidy.com/<shortlink>'
                                    className='w-full rounded border border-gray-300 px-3 py-2' />
                            </div>
                            <div>
                                <label htmlFor='campaign-donor-goal' className='mb-1 block font-semibold text-primaryDark'>
                                    Partners goal
                                </label>
                                <input id='campaign-donor-goal' type='number' name='donor_goal' min='1'
                                    value={formData.donor_goal} onChange={handleChange}
                                    className='w-full rounded border border-gray-300 px-3 py-2' />
                                <p className='mt-1 text-sm text-gray-500'>
                                    The banner's partner progress bar fills up as donors approach this number.
                                </p>
                            </div>
                            <div className='flex items-center gap-3'>
                                <input id='campaign-popup-enabled' type='checkbox' name='popup_enabled'
                                    checked={formData.popup_enabled} onChange={handleChange}
                                    className='h-5 w-5' />
                                <label htmlFor='campaign-popup-enabled' className='font-semibold text-primaryDark'>
                                    Show daily popup (banner stays on either way)
                                </label>
                            </div>
                            <div>
                                <label htmlFor='campaign-popup-image' className='mb-1 block font-semibold text-primaryDark'>
                                    Popup image URL
                                </label>
                                <input id='campaign-popup-image' type='text' name='popup_image_url'
                                    value={formData.popup_image_url} onChange={handleChange}
                                    className='w-full rounded border border-gray-300 px-3 py-2' />
                                {formData.popup_image_url && (
                                    <img src={formData.popup_image_url} alt='Popup preview' className='mt-2 max-h-40 rounded' />
                                )}
                            </div>
                            <div>
                                <label htmlFor='campaign-popup-headline' className='mb-1 block font-semibold text-primaryDark'>
                                    Popup headline
                                </label>
                                <input id='campaign-popup-headline' type='text' name='popup_headline'
                                    value={formData.popup_headline} onChange={handleChange}
                                    className='w-full rounded border border-gray-300 px-3 py-2' />
                            </div>
                            <div>
                                <label htmlFor='campaign-popup-text' className='mb-1 block font-semibold text-primaryDark'>
                                    Popup text
                                </label>
                                <textarea id='campaign-popup-text' name='popup_text' rows={4}
                                    value={formData.popup_text} onChange={handleChange}
                                    className='w-full rounded border border-gray-300 px-3 py-2' />
                            </div>
                            <button type='button' onClick={submitData} disabled={loadState !== 'loaded'}
                                className='rounded bg-primaryBlue px-8 py-2.5 font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed'>
                                Save
                            </button>
                        </div>
                    </div>
                </main>
            </div>
        </>
    )
}

export default CampaignManagement
